//* 내 편리정보(자주쓰는 장지·상주말씀) — 역할별 detail 리스트 읽기/저장 공용
//* 저장은 PATCH auth/me/detail(nonAdminUpdateUser). 스펙: docs/bugo-mourner-text-favorites-spec.md
import { nonAdminUpdateUser } from '@shared/api/user/user.controller';
import {
  MyDetailListKey,
  MyDetailListUpdate,
  User,
} from '@shared/api/user/user.interface';
import { CamelRole } from '@shared/types';

export type MyDetailKey = Exclude<keyof MyDetailListUpdate, '_id'>;

//* 관리자(admin)는 adminDetail 이 서버 스키마에 없어 저장이 안 된다 —
//* 기존 장지 편집기와 같이 bugoAgencyWorkerDetail 을 쓴다(읽기·쓰기 같은 키).
export const myDetailKey = (camelRole: CamelRole): MyDetailKey => {
  switch (camelRole) {
    case CamelRole.BugoAgencyAdmin:
      return 'bugoAgencyAdminDetail';
    case CamelRole.BugoFuneralHome:
      return 'bugoFuneralHomeDetail';
    case CamelRole.BugoCustomer:
      return 'bugoCustomerDetail';
    case CamelRole.BugoGuest:
      return 'bugoGuestDetail';
    case CamelRole.Admin:
    case CamelRole.BugoAgencyWorker:
    default:
      return 'bugoAgencyWorkerDetail';
  }
};

export const readMyDetailList = (
  userProfile: User | null | undefined,
  camelRole: CamelRole,
  key: MyDetailListKey,
): string[] => {
  if (!userProfile) return [];
  const detail = userProfile[myDetailKey(camelRole)] as
    | Partial<Record<MyDetailListKey, unknown>>
    | undefined;
  const list = detail?.[key];
  return Array.isArray(list)
    ? list.filter((v): v is string => typeof v === 'string')
    : [];
};

//* 서버 규칙과 같은 정규화(trim·빈값 제거·중복 제거·300자·30개) — 화면에서 미리 맞춰 보낸다
export const MOURNER_TEXT_MAX_LENGTH = 300;
export const MOURNER_TEXT_MAX_COUNT = 30;

export const normalizeMournerTextList = (list: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list) {
    const text = (raw ?? '').trim().slice(0, MOURNER_TEXT_MAX_LENGTH);
    if (!text || seen.has(text)) continue;
    seen.add(text);
    out.push(text);
    if (out.length >= MOURNER_TEXT_MAX_COUNT) break;
  }
  return out;
};

export interface SaveMyDetailListResult {
  user: User;
  //* 서버 응답에 해당 리스트가 실려 왔는지. false면 구서버(스키마에 필드 없음)라 저장이 안 된 것
  persisted: boolean;
}

/**
 * 리스트 전체를 보내 교체한다(개별 추가/삭제 API 없음).
 * 기존 detail 을 spread 해서 같이 보내는 이유: 새 서버는 dotted $set 이라 필요 없지만,
 * 구서버는 detail 을 통째로 덮어써 spread 없이 보내면 장지·소속이 지워진다(안전장치).
 */
export const saveMyDetailList = async (
  userProfile: User,
  camelRole: CamelRole,
  key: MyDetailListKey,
  list: string[],
): Promise<SaveMyDetailListResult> => {
  const detailKey = myDetailKey(camelRole);
  const current = (userProfile[detailKey] ?? {}) as Record<string, unknown>;
  const update: MyDetailListUpdate = {
    _id: userProfile._id,
    [detailKey]: { ...current, [key]: list },
  };
  const user = await nonAdminUpdateUser(update);
  const saved = (
    user?.[detailKey] as Partial<Record<MyDetailListKey, unknown>> | undefined
  )?.[key];
  return { user, persisted: Array.isArray(saved) };
};
